import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name.split(" ")[0]}`);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0B0F19] p-12 text-white lg:flex grid-bg">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold">
          <Shield className="h-6 w-6 text-primary" /> IShareLeads
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Your agency's command center for winning leads.</h2>
          <p className="mt-4 max-w-md text-slate-400">Life, health, casualty & final needs leads — organized, mapped by area, and shared across your team.</p>
        </div>
        <p className="font-mono text-xs text-slate-600">© 2026 IShareLeads</p>
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-24">
        <Link to="/" className="mb-8 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
        <h1 className="font-heading text-3xl font-bold">Agency portal login</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in to manage your leads.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} required data-testid="login-email-input"
              onChange={(e) => setEmail(e.target.value)} placeholder="you@agency.com" className="mt-1.5" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <Input id="password" type="password" value={password} required data-testid="login-password-input"
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
        </div>

        <Button variant="outline" onClick={startGoogle} data-testid="google-login-button" className="w-full">
          <img src="https://www.google.com/favicon.ico" alt="" className="mr-2 h-4 w-4" /> Continue with Google
        </Button>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          New agency?{" "}
          <Link to="/register" className="font-medium text-primary hover:underline" data-testid="go-register-link">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
